(() => {
  const formatTime = seconds => {
    if (!Number.isFinite(seconds)) return '–:––';
    const value = Math.max(0, Math.floor(seconds));
    return `${Math.floor(value / 60)}:${String(value % 60).padStart(2, '0')}`;
  };

  function initMedia(root) {
    const audioElements = [...root.querySelectorAll('[data-edition-audio]')];
    const videoStops = new Map();
    const stopVideos = except => videoStops.forEach((stop, frame) => { if (frame !== except) stop(); });
    const pauseAudio = except => audioElements.forEach(audio => { if (audio !== except) audio.pause(); });

    root.querySelectorAll('[data-video-id]').forEach(frame => {
      const poster = frame.querySelector('[data-play-video]')?.cloneNode(true);
      if (!poster) return;
      videoStops.set(frame, () => {
        if (frame.querySelector('iframe')) frame.replaceChildren(poster.cloneNode(true));
      });
      frame.addEventListener('click', event => {
        if (!event.target.closest('[data-play-video]')) return;
        const id = frame.dataset.videoId;
        if (!/^[\w-]{11}$/.test(id)) return;
        pauseAudio();
        stopVideos(frame);
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`;
        iframe.title = frame.dataset.videoTitle || 'Performance of this edition';
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
        iframe.allowFullscreen = true;
        iframe.referrerPolicy = 'strict-origin-when-cross-origin';
        frame.replaceChildren(iframe);
      });
    });

    root.querySelectorAll('[data-audio-player]').forEach(player => {
      const audio = player.querySelector('audio');
      const toggle = player.querySelector('[data-audio-toggle]');
      const seek = player.querySelector('[data-audio-seek]');
      const elapsed = player.querySelector('[data-audio-elapsed]');
      const duration = player.querySelector('[data-audio-duration]');
      const status = player.querySelector('[data-audio-status]');
      let failed = false;
      let seeking = false;
      const message = text => { status.textContent = text; status.hidden = !text; };
      const updatePlayback = () => {
        const playing = !audio.paused && !audio.ended;
        toggle.setAttribute('aria-label', failed ? 'Retry recording' : playing ? 'Pause recording' : 'Play recording');
        player.querySelector('[data-play-icon]').hidden = playing;
        player.querySelector('[data-pause-icon]').hidden = !playing;
      };
      const updateTimeline = () => {
        const total = Number.isFinite(audio.duration) ? audio.duration : 0;
        const current = seeking ? Number(seek.value) : audio.currentTime;
        seek.max = total;
        seek.disabled = total <= 0;
        if (!seeking) seek.value = current;
        seek.style.setProperty('--audio-progress', `${total ? Math.min(100, current / total * 100) : 0}%`);
        seek.setAttribute('aria-valuetext', `${formatTime(current)} of ${formatTime(audio.duration)}`);
        elapsed.textContent = formatTime(current);
        duration.textContent = formatTime(audio.duration);
      };
      toggle.addEventListener('click', () => {
        if (!audio.paused && !failed) { audio.pause(); return; }
        if (failed) { failed = false; audio.load(); }
        message('');
        if (audio.ended) audio.currentTime = 0;
        audio.play().catch(error => {
          if (error.name === 'AbortError') return;
          failed = true;
          message('The recording could not play. Please try again.');
          updatePlayback();
        });
      });
      seek.addEventListener('input', () => { seeking = true; updateTimeline(); });
      seek.addEventListener('change', () => {
        if (Number.isFinite(audio.duration)) audio.currentTime = Number(seek.value);
        seeking = false;
        updateTimeline();
      });
      ['loadedmetadata', 'durationchange', 'timeupdate', 'seeked'].forEach(name => audio.addEventListener(name, updateTimeline));
      ['play', 'pause', 'ended'].forEach(name => audio.addEventListener(name, updatePlayback));
      audio.addEventListener('play', () => { pauseAudio(audio); stopVideos(); });
      audio.addEventListener('playing', () => { failed = false; message(''); updatePlayback(); });
      audio.addEventListener('error', () => {
        failed = true;
        message('The recording could not load. Please try again.');
        updatePlayback();
      });
      updatePlayback();
      updateTimeline();
      audio.controls = false;
      audio.hidden = true;
      player.querySelector('[data-audio-controls]').hidden = false;
    });

    root.querySelectorAll('[data-performance-item]').forEach(media => {
      const tablist = media.querySelector('[data-media-tabs]');
      if (!tablist) return;
      const tabs = [...tablist.querySelectorAll('[data-media-tab]')];
      const panels = [...media.querySelectorAll('[data-media-panel]')];
      const select = selected => {
        const type = selected.dataset.mediaTab;
        if (type === 'video') pauseAudio();
        else stopVideos();
        tabs.forEach(tab => {
          const active = tab === selected;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.dataset.mediaPanel !== type; });
      };
      panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabs.find(tab => tab.dataset.mediaTab === panel.dataset.mediaPanel).id);
      });
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) { event.preventDefault(); next.focus(); select(next); }
        });
      });
      tablist.hidden = false;
    });

    root.querySelectorAll('[data-performance-media]').forEach(media => {
      const tablist = media.querySelector('[data-movement-tabs]');
      if (!tablist) return;
      const tabs = [...tablist.querySelectorAll('[data-movement-tab]')];
      const panels = [...media.querySelectorAll(':scope > [data-movement-panel]')];
      const select = selected => {
        const movement = selected.dataset.movementTab;
        pauseAudio();
        stopVideos();
        tabs.forEach(tab => {
          const active = tab === selected;
          tab.setAttribute('aria-selected', String(active));
          tab.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.dataset.movementPanel !== movement; });
      };
      panels.forEach(panel => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tabs.find(tab => tab.dataset.movementTab === panel.dataset.movementPanel).id);
      });
      tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => select(tab));
        tab.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
          if (event.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
          if (event.key === 'Home') next = tabs[0];
          if (event.key === 'End') next = tabs[tabs.length - 1];
          if (next) { event.preventDefault(); next.focus(); select(next); }
        });
      });
    });
  }

  initMedia(document);
  window.addEventListener('concert-scores:content-replaced', event => initMedia(event.detail?.root || document));
  window.addEventListener('pagehide', () => document.querySelectorAll('[data-edition-audio]').forEach(audio => audio.pause()));
})();
